import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUnmatchedQueue, useVisits } from '@/hooks/use-visits';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { customerService } from '@/lib/packages';
import { CustomerRow } from '@/integrations/supabase/types';
import { 
  Users, 
  Clock, 
  MapPin, 
  UserCheck, 
  UserPlus, 
  Link,
  Package,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Search,
  FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface TemporaryVisit {
  id: string;
  visitId: string;
  timestamp: string;
  location: string;
  visitType: string;
  notes: string;
  productId?: string;
}


const visitTypeLabels = {
  'inceledi': 'Ürünü İnceledi',
  'fiyat_talep': 'Fiyat Talep Etti', 
  'teklif_verildi': 'Teklif Verildi',
  'satis_yapildi': 'Satış Yapıldı',
};

const visitTypeColors = {
  'inceledi': 'bg-blue-500/20 text-blue-600',
  'fiyat_talep': 'bg-yellow-500/20 text-yellow-600',
  'teklif_verildi': 'bg-orange-500/20 text-orange-600',
  'satis_yapildi': 'bg-green-500/20 text-green-600',
};


const UnmatchedQueue = () => {
  const navigate = useNavigate();
  const { companyId } = useAuth();
  const { toast } = useToast();
  
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showBindModal, setShowBindModal] = useState(false);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<TemporaryVisit | null>(null);
  const [isBulkBindMode, setIsBulkBindMode] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  
  // Database states for customer search
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  
  const {
    unmatchedItems,
    createNewCustomer,
    ignoreItem,
    bulkLink,
    isCreating,
    isIgnoring,
    isBulkLinking,
  } = useUnmatchedQueue();
  
  const { linkVisitToCustomer, isLinkingVisit } = useVisits();

  const getCustomerName = (customer: CustomerRow) => {
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri';
  };

  // Debounced search for customers
  const searchCustomers = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setCustomers([]);
      return;
    }

    setLoadingCustomers(true);
    try {
      const results = await customerService.searchCustomers(companyId, searchTerm, 20);
      setCustomers(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      toast({
        title: "Hata",
        description: "Müşteri arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingCustomers(false);
    }
  }, [companyId, toast]);

  // Effect for customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = now.getTime() - time.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}s ${minutes}d önce`;
    }
    return `${minutes}d önce`;
  };

  const filteredCustomers = customers.filter(customer => 
    getCustomerName(customer).toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.email?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleSelectItem = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === unmatchedItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(unmatchedItems.map(item => item.id));
    }
  };

  const handleBindToCustomer = (visit: TemporaryVisit) => {
    setIsBulkBindMode(false);
    setSelectedVisit(visit);
    setCustomerSearch('');
    setCustomers([]);
    setShowCustomerResults(false);
    setShowBindModal(true);
  };

  const handleCreateAndBind = (visit: TemporaryVisit) => {
    setSelectedVisit(visit);
    setShowCreateCustomerModal(true);
  };

  const handleConfirmBind = async (customerId: string) => {
    if (isBulkBindMode && selectedItems.length > 0) {
      bulkLink(
        { itemIds: selectedItems, customerId },
        {
          onSuccess: () => {
            setShowBindModal(false);
            setSelectedVisit(null);
            setIsBulkBindMode(false);
            setSelectedItems([]);
            setCustomerSearch('');
            setCustomers([]);
            setShowCustomerResults(false);
          },
        }
      );
      return;
    }

    if (selectedVisit) {
      console.log('Binding visit', selectedVisit.visitId, 'to customer', customerId);
      try {
        linkVisitToCustomer({ visitId: selectedVisit.visitId, customerId }, {
          onSuccess: () => {
            // Close modal and reset state after successful binding
            setShowBindModal(false);
            setSelectedVisit(null);
            setCustomerSearch('');
            setCustomers([]);
            setShowCustomerResults(false);
          }
        });
      } catch (error) {
        console.error('Error in handleConfirmBind:', error);
      }
    }
  };

  const handleConfirmCreateCustomer = () => {
    if (selectedVisit && newCustomerName.trim()) {
      // In real app, this would create a new customer and bind the visit
      console.log('Creating customer and binding visit', selectedVisit.id);
      createNewCustomer(selectedVisit.id);
      setShowCreateCustomerModal(false);
      setSelectedVisit(null);
      setNewCustomerName('');
      setNewCustomerEmail('');
    }
  };

  const handleBulkBind = () => {
    if (selectedItems.length > 0) {
      setSelectedVisit(null);
      setIsBulkBindMode(true);
      setCustomerSearch('');
      setCustomers([]);
      setShowCustomerResults(false);
      setShowBindModal(true);
    }
  };

  if (unmatchedItems.length === 0) {
    return (
      <Layout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Geçici Ziyaret Kayıtları</h1>
              <p className="text-muted-foreground">Müşteri ile eşleştirilmemiş ziyaret kayıtları</p>
            </div>
          </div>

          {/* Empty State */}
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 mx-auto text-success mb-4" />
                <h3 className="text-xl font-semibold mb-2">Tüm Kayıtlar Eşleştirildi!</h3>
                <p className="text-muted-foreground">
                  Şu anda eşleştirilmemiş hiçbir geçici ziyaret kaydı bulunmuyor.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Geçici Ziyaret Kayıtları</h1>
              <p className="text-muted-foreground">
                {unmatchedItems.length} eşleşmemiş ziyaret kaydı
              </p>
            </div>
          </div>

          {selectedItems.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedItems.length} seçili
              </span>
              <Button 
                onClick={handleBulkBind} 
                className="gap-2"
                disabled={isBulkLinking}
              >
                {isBulkLinking ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Link className="w-4 h-4" />
                )}
                {isBulkLinking ? 'Bağlanıyor...' : 'Toplu Müşteri Bağla'}
              </Button>
            </div>
          )}
        </div>

        {/* Bulk Selection */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={selectedItems.length === unmatchedItems.length}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm font-medium">Tümünü Seç</span>
              {selectedItems.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({selectedItems.length}/{unmatchedItems.length})
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Temporary Visits */}
        <div className="space-y-4">
          {unmatchedItems.map((visit) => (
            <Card key={visit.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedItems.includes(visit.id)}
                      onCheckedChange={() => handleSelectItem(visit.id)}
                    />
                    
                    <div className="w-16 h-16 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-8 h-8 text-accent" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(visit.timestamp)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {visit.location}
                          </span>
                        </div>
                      </div>

                      {/* Visit Type */}
                      <div className="flex items-center gap-2 mb-3">
                        <Badge 
                          variant="secondary" 
                          className={visitTypeColors[visit.visitType] || 'bg-muted/20 text-muted-foreground'}
                        >
                          {visitTypeLabels[visit.visitType] || visit.visitType}
                        </Badge>
                        <Badge variant="outline">Geçici Kayıt</Badge>
                      </div>

                      {/* Notes */}
                      {visit.notes && (
                        <div className="mb-3">
                          <p className="text-sm text-muted-foreground flex items-start gap-1">
                            <FileText className="w-3 h-3 mt-0.5" />
                            {visit.notes}
                          </p>
                        </div>
                      )}

                      {/* Product Info */}
                      {visit.productId && (
                        <div className="mb-3">
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Package className="w-3 h-3" />
                            Ürün ID: {visit.productId}
                          </p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleBindToCustomer(visit)}
                          className="gap-1"
                          disabled={isLinkingVisit}
                        >
                          {isLinkingVisit ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <UserCheck className="w-3 h-3" />
                          )}
                          Müşteriye Bağla
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleCreateAndBind(visit)}
                          className="gap-1"
                          disabled={isCreating}
                        >
                          {isCreating ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <UserPlus className="w-3 h-3" />
                          )}
                          Yeni Müşteri Oluştur
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/visits/${visit.visitId}`)}
                          className="gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          Detayları Gör
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Customer Bind Modal */}
        <Dialog 
          open={showBindModal} 
          onOpenChange={(open) => {
            setShowBindModal(open);
            if (!open) {
              setIsBulkBindMode(false);
              setCustomerSearch('');
              setCustomers([]);
              setShowCustomerResults(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Müşteri Seç ve Bağla</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {isBulkBindMode
                  ? `Seçili ${selectedItems.length} ziyaret kaydını hangi müşteriye bağlamak istiyorsunuz?`
                  : 'Bu ziyaret kaydını hangi müşteriye bağlamak istiyorsunuz?'}
              </p>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Müşteri ara..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerResults(e.target.value.length > 0);
                    if (e.target.value === '') {
                      setCustomers([]);
                    }
                  }}
                  onFocus={() => setShowCustomerResults(customerSearch.length > 0)}
                  onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
                  className="pl-10"
                />
                
                {showCustomerResults && (
                  <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto backdrop-blur-sm">
                    {loadingCustomers ? (
                      <div className="p-3 text-muted-foreground text-center">
                        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                        Müşteriler aranıyor...
                      </div>
                    ) : customers.length > 0 ? (
                      customers.map((customer) => (
                        <div
                          key={customer.id}
                          className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                          onClick={() => {
                            handleConfirmBind(customer.id);
                            setShowCustomerResults(false);
                            setCustomerSearch('');
                          }}
                        >
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-accent/20 text-accent text-xs">
                              {getCustomerName(customer).split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{getCustomerName(customer)}</p>
                            <p className="text-sm text-muted-foreground">{customer.email}</p>
                          </div>
                        </div>
                      ))
                    ) : customerSearch.length >= 2 ? (
                      <div className="p-3 text-muted-foreground text-center">
                        Müşteri bulunamadı
                      </div>
                    ) : (
                      <div className="p-3 text-muted-foreground text-center">
                        En az 2 karakter girin
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBindModal(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Customer Modal */}
        <Dialog open={showCreateCustomerModal} onOpenChange={setShowCreateCustomerModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni Müşteri Oluştur</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="customerName">Müşteri Adı *</Label>
                <Input
                  id="customerName"
                  placeholder="Müşteri adını girin..."
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="customerEmail">E-posta</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  placeholder="E-posta adresi..."
                  value={newCustomerEmail}
                  onChange={(e) => setNewCustomerEmail(e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateCustomerModal(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
                <Button 
                  onClick={handleConfirmCreateCustomer}
                  disabled={!newCustomerName.trim() || isCreating}
                  className="flex-1 gap-2"
                >
                  {isCreating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  {isCreating ? 'Oluşturuluyor...' : 'Oluştur ve Bağla'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UnmatchedQueue;